import React, { useRef } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery } from '@apollo/client';
import { BOOKING_POLICY_RECORD } from '../../../../../../services/policyService/queries';
import { AgreementRecordView } from './AgreementRecordView';
import { AmendmentsList } from './AmendmentsList';
import {
  IGetBookingQuery,
  useGetBookingQuery,
} from '../../../../../../services/bookingService/__generated__/queries.generated';
import { useParams } from 'react-router-dom';
import { useBookingLocale } from '../../../../../../hooks/useBookingLocale';
import { currencyCodeSuffix } from '../../../../../../utils/money';
import { asDistanceUnit, convertLimit } from '../../../../../../utils/units';
import _ from 'lodash';
import LoadingSpinner from '../../../../../../components/LoadingSpinner';
import { DateTime } from 'luxon';
import { formatAddress } from '../../../../../../helpers/formatAddress';
import { formatInstantInCarTimezone } from '../../../../../../helpers/timezoneHelpers';
import { useReactToPrint } from 'react-to-print';
import {
  brandHasPolicy,
  brandPolicyPath,
  brandPolicyTitle,
  getBrand,
} from '../../../../../../constants/brands';
import { PolicyMarkdown, usePolicyMarkdown } from '../../../../../PolicyPage/policyMarkdown';

const PageContainer = styled.div`
  ${tw`w-full max-w-full min-w-0 h-full flex flex-col items-center box-border`}

  @media print {
    width: 100% !important;
    height: auto !important;
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const AgreementContainer = styled.div`
  ${tw`p-8 space-y-6 min-w-[800px] max-h-[80vh] overflow-y-auto`}

  @media print {
    max-height: none !important;
    overflow: visible !important;
    height: auto !important;
    min-height: auto !important;
    width: 100% !important;
    min-width: auto !important;
    padding: 0 15px !important;
    padding-top: 0 !important;
    page-break-inside: avoid;
  }
`;

const Container = styled.div`
  ${tw`flex justify-center items-center h-full w-full max-w-screen-xl`}

  @media print {
    width: 100% !important;
    height: auto !important;
    display: block !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const Header = styled.div`
  ${tw`text-center mb-8`}

  @media print {
    margin-top: 0 !important;
    margin-bottom: 8px !important;
    padding-top: 0 !important;
  }
`;

const AgreementTitle = styled.h1`
  ${tw`text-3xl font-bold mb-4`}

  @media print {
    font-size: 20px !important;
    margin-top: 0 !important;
    margin-bottom: 8px !important;
    padding-top: 0 !important;
  }
`;

const PreHeader = styled.div`
  ${tw`mb-6 text-center`}

  @media print {
    margin-top: 0 !important;
    margin-bottom: 8px !important;
    padding-top: 0 !important;
  }
`;

const Section = styled.section`
  ${tw`mb-8`}

  @media print {
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    page-break-inside: avoid;
    break-inside: avoid;
  }
`;

const SectionTitle = styled.h2`
  ${tw`text-xl font-bold mb-2`}

  @media print {
    font-size: 14px !important;
    margin-bottom: 4px !important;
    margin-top: 0 !important;
  }
`;

const Text = styled.p`
  ${tw`text-sm leading-relaxed whitespace-pre-wrap`}

  @media print {
    font-size: 10px !important;
    line-height: 1.3 !important;
    margin-bottom: 6px !important;
    page-break-inside: avoid;
    orphans: 3;
    widows: 3;
  }
`;

const PrintButton = styled.button`
  ${tw`w-[300px] px-4 py-2 bg-black text-white rounded-md mx-auto block my-20 max-w-full min-w-0 box-border`}

  @media print {
    display: none !important;
  }
`;

const PriceBreakdownText = styled(Text)`
  ${tw`mt-4`}

  @media print {
    margin-top: 6px !important;
  }
`;

const IndentedText = styled(Text)`
  ${tw`ml-5`}

  @media print {
    margin-left: 10px !important;
  }
`;

const SignatureSection = styled.div`
  ${tw`flex justify-between mt-10 pt-5 border-t border-border-default`}

  @media print {
    margin-top: 10px !important;
    padding-top: 6px !important;
    border-top: 1px solid #ccc !important;
  }
`;

const SignatureContainer = styled.div`
  ${tw`flex-1`}
`;

const SignatureLine = styled.div`
  ${tw`mt-48 border-t border-black w-[300px]`}

  @media print {
    margin-top: 15px !important;
    border-top: 1px solid #000 !important;
    width: 300px !important;
  }
`;

const DateContainer = styled.div`
  ${tw`flex-1 text-right`}
`;

const MarginTop10 = styled.div`
  ${tw`mt-10`}

  @media print {
    margin-top: 40px !important;
  }
`;

const DateLine = styled.div`
  ${tw`mt-48 border-t border-black w-[300px] ml-auto`}

  @media print {
    margin-top: 15px !important;
    border-top: 1px solid #000 !important;
    width: 300px !important;
    margin-left: auto !important;
  }
`;

// Helper function to extract out-of-pocket maximum from policyDetails
const extractOutOfPocketMax = (policyDetails?: string | null): string | null => {
  if (!policyDetails) return null;

  // Try to extract dollar amount from patterns like "$3000", "$500", "Out of pocket - $3000"
  const patterns = [
    /\$(\d+(?:,\d{3})*)/g, // Matches $3000, $500, $1,000
    /out.*pocket.*?\$(\d+(?:,\d{3})*)/i, // Matches "out of pocket - $3000"
    /max.*out.*pocket.*?\$(\d+(?:,\d{3})*)/i, // Matches "$500 max out-of-pocket"
  ];

  for (const pattern of patterns) {
    const matches = policyDetails.match(pattern);
    if (matches && matches.length > 0) {
      // Get the last match (most specific)
      const lastMatch = matches[matches.length - 1];
      const amount = lastMatch.replace(/\$/g, '').replace(/,/g, '');
      return amount;
    }
  }

  return null;
};

export const RentalAgreement = () => {
  const { bookingId } = useParams();
  // Spec 028: the trip's unit.
  const bookingLocale = useBookingLocale(bookingId, 'any');
  // A brand with its own renter agreement (Lenvoy) shows it under the trip summary; any
  // other brand shows the default car sharing agreement below, with its own names.
  const brand = getBrand();
  const company = brand.name;
  const legalEntity = brand.legalName;
  const usesBrandAgreement = brandHasPolicy('renter-agreement', brand);
  const brandAgreementTitle = brandPolicyTitle('renter-agreement', brand);
  const brandAgreement = usePolicyMarkdown(
    usesBrandAgreement ? brandPolicyPath('renter-agreement', brand) : null,
    brandAgreementTitle,
  );
  const { data: bookingData, loading: bookingLoading } = useGetBookingQuery({
    variables: { id: bookingId || '' },
    skip: !bookingId,
  });
  const { data: policyData, loading: policyLoading } = useQuery(BOOKING_POLICY_RECORD, {
    variables: { id: bookingId || '' },
    skip: !bookingId,
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  });
  const agreementRecords = [...(policyData?.booking?.agreementRecords ?? [])].sort(
    (a: { sequence: number }, b: { sequence: number }) => a.sequence - b.sequence,
  );
  const firstRecord = agreementRecords[0];

  const booking = bookingData?.booking || ({} as IGetBookingQuery['booking']);

  const timezone = booking?.car?.location?.timezone ?? 'America/Los_Angeles';

  // Format dates
  const formatDateTime = (timestamp?: number | null): string => {
    if (!timestamp) return 'null';
    return DateTime.fromMillis(timestamp).setZone(timezone).toFormat('MM-dd-yyyy hh:mm a');
  };

  const formatDate = (timestamp?: number | null): string => {
    if (!timestamp) return 'null';
    return DateTime.fromMillis(timestamp).setZone(timezone).toFormat('EEE, MMM dd, yyyy hh:mm a');
  };

  // Get booking data
  const bookedOn = booking?.createdAt
    ? formatDateTime(
        typeof booking.createdAt === 'string'
          ? new Date(booking.createdAt).getTime()
          : booking.createdAt,
      )
    : 'null';
  const reservationId = booking?.id || 'null';
  const vehicle = booking?.car
    ? `${booking.car.year || 'null'} ${booking.car.make || 'null'} ${booking.car.model || 'null'}`
    : 'null';
  const hostName = booking?.host?.displayName || 'null';
  const primaryDriver = booking?.guest?.displayName || 'null';
  const tripStart = formatDate(booking?.startAt);
  const tripEnd = formatDate(booking?.endAt);
  const licensePlate = booking?.car?.licensePlate || 'null';
  const vin = booking?.car?.vin || 'null';

  // Get location data directly from booking (already loaded via relations)
  const pickupLocation = (booking as any)?.pickupLocation;
  const returnLocation = (booking as any)?.returnLocation;
  const pickupLocationDisplay = formatAddress(pickupLocation) || 'null';
  const returnLocationDisplay = formatAddress(returnLocation) || 'null';

  // Distance included, in the trip's unit (spec 028)
  const totalDays = booking?.receipt?.totalDays || 0;
  const tripUnit = bookingLocale.distanceUnit;
  const dailyDistanceLimit = convertLimit(
    booking?.car?.distanceLimit?.dailyDistanceLimit || 0,
    asDistanceUnit((booking?.car?.distanceLimit as any)?.unit),
    tripUnit,
  );
  const unlimitedMileage = (booking?.car?.distanceLimit as any)?.unlimitedMileage || false;
  const milesIncluded = unlimitedMileage
    ? 'Unlimited'
    : totalDays * dailyDistanceLimit > 0
      ? `${(totalDays * dailyDistanceLimit).toLocaleString('en-US')} ${tripUnit === 'km' ? 'km' : 'mi'}`
      : 'null';

  // Get additional drivers (name + added time in vehicle local timezone)
  const carTz = booking?.car?.location?.timezone ?? undefined;
  const addedAtByDriverId = new Map<string, string | number | undefined>(
    (booking?.additionalDrivers ?? []).map(row => [String(row.driverId ?? ''), row.createdAt]),
  );
  const additionalDrivers = booking?.additionalDriverDetails || [];
  const additionalDriversDisplay =
    additionalDrivers.length > 0
      ? additionalDrivers
          .map(d => {
            const name = `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'Unknown';
            const added = formatInstantInCarTimezone(addedAtByDriverId.get(String(d.id)), carTz);
            return `${name} (added ${added})`;
          })
          .join('; ')
      : 'None';

  // Get price breakdown
  const receipt = booking?.receipt;
  const dailyRate = receipt?.pricePerDay || 0;
  const taxesAndFees = (receipt?.tax || 0) + (receipt?.tripFeeTotal || 0);
  const total = receipt?.totalPrice || 0;

  // Use protection plan data from the booking (eagerly loaded via relations)
  const currentProtectionPlan = booking?.protectionPlanEntity;
  const protectionPlan = currentProtectionPlan?.plan || booking?.protectionPlan || 'null';
  const outOfPocketMax = (currentProtectionPlan as any)?.outOfPocket;
  const outOfPocketMaxDisplay =
    outOfPocketMax !== null && outOfPocketMax !== undefined
      ? `$${Number(outOfPocketMax).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : 'null';
  const liabilityCoverage = currentProtectionPlan?.policyDetails
    ? (currentProtectionPlan.policyDetails || '').replace(/<[^>]*>/g, '').trim() ||
      'State minimum liability protection'
    : 'State minimum liability protection';

  // Create ref for the AgreementContainer to print
  const printRef = useRef<HTMLDivElement>(null);

  // Configure react-to-print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: usesBrandAgreement ? brandAgreementTitle : 'Rental Agreement',
    pageStyle: `
      @page {
        margin: 0.3cm 0.8cm 0cm 0.8cm;
        size: letter;
        margin-bottom: 0cm !important;
      }

      @page:first {
        margin-top: 0.3cm !important;
        margin-bottom: 0cm !important;
      }

      @page:left {
        margin-bottom: 0cm !important;
      }

      @page:right {
        margin-bottom: 0cm !important;
      }

      @media print {
        /* Hide navigation and other UI elements */
        nav,
        header,
        .navigation,
        .navbar,
        .header,
        [class*="NavBar"],
        [class*="navBar"] {
          display: none !important;
        }

        /* Ensure body and root take full space */
        body,
        #root {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          height: auto !important;
          font-size: 10px !important;
        }

        /* Remove any top spacing from first element */
        #root > *:first-child,
        body > *:first-child {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }

        /* Ensure all content is visible */
        * {
          visibility: visible !important;
        }

        /* Reduce spacing for all elements */
        h1, h2, h3, h4, h5, h6 {
          margin-top: 0 !important;
          margin-bottom: 4px !important;
        }

        /* Section titles should have minimal top margin */
        section > h2:first-child {
          margin-top: 0 !important;
        }

        p, div, span {
          margin-top: 0 !important;
          margin-bottom: 4px !important;
        }

        /* Remove all spacing between sections */
        section {
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        }

        /* Ensure no spacing between adjacent sections */
        section + section {
          margin-top: 0 !important;
        }

        /* Reduce inline style margins */
        [style*="marginTop"] {
          margin-top: 6px !important;
        }

        [style*="marginLeft"] {
          margin-left: 10px !important;
        }

        /* Optimize signature section */
        #signature {
          margin-top: 10px !important;
          padding-top: 6px !important;
        }

        #signature div[style*="marginTop"] {
          margin-top: 15px !important;
        }

        /* Reduce strong tag spacing */
        strong {
          font-weight: 600 !important;
        }

        /* Optimize list spacing */
        ul, ol {
          margin-top: 4px !important;
          margin-bottom: 4px !important;
          padding-left: 20px !important;
        }

        li {
          margin-bottom: 2px !important;
        }
      }
    `,
  });

  if (
    bookingLoading ||
    policyLoading ||
    (usesBrandAgreement && !firstRecord && brandAgreement.loading)
  ) {
    return <LoadingSpinner />;
  }

  if (_.isEmpty(booking)) {
    return <Container>No booking found</Container>;
  }

  const tripSummary = (
    <Section id="trip-summary">
      <SectionTitle>
        {primaryDriver}'s trip with {company}
      </SectionTitle>
      <Text>
        <strong>Primary driver:</strong> {primaryDriver}
      </Text>
      <Text>
        <strong>Additional drivers:</strong> {additionalDriversDisplay}
      </Text>
      <Text>
        <strong>
          {tripUnit === 'km' ? 'Distance' : 'Miles'} included for entire trip duration:
        </strong>{' '}
        {milesIncluded}
      </Text>
      {total > 0 && (
        <>
          <PriceBreakdownText>
            <strong>Price breakdown:</strong>
          </PriceBreakdownText>
          <IndentedText>
            <strong>Daily rate:</strong> $
            {dailyRate.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            /day x {totalDays} days = $
            {(dailyRate * totalDays).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </IndentedText>
          {taxesAndFees > 0 && (
            <IndentedText>
              <strong>Taxes and fees:</strong> $
              {taxesAndFees.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </IndentedText>
          )}
          <IndentedText>
            <strong>Total:</strong> $
            {total.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            {currencyCodeSuffix(bookingLocale.currency)}
          </IndentedText>
        </>
      )}
      <PriceBreakdownText>
        <strong>Protection plan:</strong> {protectionPlan}
      </PriceBreakdownText>
      <Text>
        <strong>Out-of-pocket maximum:</strong> {outOfPocketMaxDisplay}
      </Text>
      <Text>
        <strong>Liability coverage:</strong> {liabilityCoverage}
      </Text>
      <Text>
        <strong>Booked on:</strong> {bookedOn}
      </Text>
      <Text>
        <strong>Reservation ID #:</strong> {reservationId}
      </Text>
      <Text>
        <strong>Vehicle:</strong> {vehicle}
      </Text>
      <Text>
        <strong>Host (car owner or authorized representative):</strong> {hostName}
      </Text>
      <Text>
        <strong>License plate:</strong> {licensePlate}
      </Text>
      <Text>
        <strong>VIN:</strong> {vin}
      </Text>
      <Text>
        <strong>Trip start:</strong> {tripStart}
      </Text>
      <Text>
        <strong>Trip end:</strong> {tripEnd}
      </Text>
      <Text>
        <strong>Pickup location:</strong> {pickupLocationDisplay}
      </Text>
      <Text>
        <strong>Return location:</strong> {returnLocationDisplay}
      </Text>
    </Section>
  );

  if (firstRecord) {
    const governing =
      policyData?.booking?.governingAgreement?.version ?? firstRecord.governingVersion;
    return (
      <PageContainer>
        <Container>
          <AgreementContainer ref={printRef}>
            <Header>
              <AgreementTitle>{`${company} rental agreement`.toUpperCase()}</AgreementTitle>
            </Header>
            <AgreementRecordView
              payload={firstRecord.payload}
              version={governing}
              acceptances={policyData?.booking?.acceptedPolicies ?? []}
            />
            <AmendmentsList records={agreementRecords} />
          </AgreementContainer>
        </Container>
        <PrintButton onClick={handlePrint}>Print Agreement</PrintButton>
      </PageContainer>
    );
  }

  // T056 waits until `yarn policies:import-files` has run on staging. Until then,
  // a trip with no record keeps this live summary and the hard-coded agreement text.
  if (usesBrandAgreement) {
    return (
      <PageContainer>
        <Container>
          <AgreementContainer ref={printRef}>
            <Header>
              <AgreementTitle>{`${company} ${brandAgreementTitle}`.toUpperCase()}</AgreementTitle>
            </Header>
            <PreHeader>
              <Text data-testid="pre-version-agreement">
                This trip was booked before versioned agreements.
              </Text>
              <Text>
                Please refer to this document if you need evidence of your transaction during or
                after your trip (for instance, when interacting with law enforcement, coverage
                providers, roadside service providers, impound lot attendants, etc.).
              </Text>
              <Text>
                The Trip Summary below is the rental confirmation for this trip. The {company}{' '}
                {brandAgreementTitle} that follows governs it.
              </Text>
            </PreHeader>
            {tripSummary}
            <Section id="brand-agreement">
              {brandAgreement.error ? (
                <Text>{brandAgreement.error}</Text>
              ) : (
                <PolicyMarkdown content={brandAgreement.content} />
              )}
            </Section>
          </AgreementContainer>
        </Container>
        <PrintButton onClick={handlePrint}>Print Agreement</PrintButton>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer>
        <Container>
          <AgreementContainer ref={printRef}>
            <Header>
              <AgreementTitle>{`${company} car sharing agreement`.toUpperCase()}</AgreementTitle>
            </Header>

            <PreHeader>
              <Text data-testid="pre-version-agreement">
                This trip was booked before versioned agreements.
              </Text>
              <Text>
                Please refer to this document if you need evidence of your transaction during or
                after your trip (for instance, when interacting with law enforcement, coverage
                providers, roadside service providers, impound lot attendants, etc.).
              </Text>
              <Text>
                The person or entity identified as "Host" and the person identified as "Guest" on
                the Trip Summary below were connected online through a website, mobile application,
                and/or associated services provided by {legalEntity} (collectively, the "{company}{' '}
                Services"). As part of connecting through the {company} Services, the Host and Guest
                agreed to be bound by the {company} Terms of Service and incorporated policies.
              </Text>
              <Text>
                There is important information below that you should review and understand,
                regardless of whether you are a Guest or Host. The above Trip Summary and this Car
                Sharing Agreement are collectively referred to as the "Agreement" and relate to the
                Trip detailed in the Trip Summary.
              </Text>
            </PreHeader>

            {/* 1. FAST FACTS */}
            <Section id="fast-facts">
              <SectionTitle>Fast facts</SectionTitle>
              <Text>
                The person named below was given permission to drive the vehicle listed below during
                the reservation period (see "Trip start" and "Trip end") by the car rental company,{' '}
                {company}, through the terms of this agreement.
              </Text>
            </Section>

            {/* 2. COVERAGE */}
            <Section id="coverage">
              <SectionTitle>Coverage</SectionTitle>
              <Text>
                {company} offers supplementary protection plans with varying levels of out-of-pocket
                maximums, which the guest may or may not have purchased. The guest is financially
                responsible for any covered damage to the vehicle, up to their purchased protection
                plan's out-of-pocket maximum listed below, regardless of whether or not the damage
                was their fault.
              </Text>
            </Section>

            {/* 2.5. PLEASE NOTE */}
            <Section id="please-note">
              <SectionTitle>Please note</SectionTitle>
              <Text>
                The guest is financially responsible for any covered damage to the vehicle, up to
                their purchased protection plan's out-of-pocket maximum listed below, regardless of
                whether or not the damage was their fault.
              </Text>
            </Section>

            {tripSummary}
            {/* 4. DEFINITIONS */}
            <Section id="definitions">
              <SectionTitle>1. DEFINITIONS</SectionTitle>
              <Text>
                <strong>a. "Authorized Driver(s)"</strong> refers to any individual, in addition to
                the Guest, authorized by the Company to operate the Vehicle and identified on the
                Trip Summary.
              </Text>
              <Text>
                <strong>b. "Extras"</strong> includes, but is not limited to, optional child seats,
                global positioning systems, ski racks, toll transponders, and other
                products/services selected by Guests.
              </Text>
              <Text>
                <strong>c. "Guest"</strong> is defined as the person or entity identified on the
                Trip Summary as the "PRIMARY DRIVER."
              </Text>
              <Text>
                <strong>d. "Company"</strong> is identified as {company}.
              </Text>
              <Text>
                <strong>e. "Reservation Period"</strong> is the duration from when the Guest takes
                possession of the Vehicle until it is returned or recovered and checked in by the
                Guest.
              </Text>
              <Text>
                <strong>f. "Vehicle"</strong> refers to the "VEHICLE" identified on the Trip
                Summary.
              </Text>
            </Section>

            {/* 5. TERMS */}
            <Section id="terms">
              <SectionTitle>2. TERMS</SectionTitle>
              <Text>
                <strong>2.1 Ownership.</strong> The Vehicle and any Extras are, by ownership,
                beneficial interest or lease, property of Host.
              </Text>
              <Text>
                <strong>2.2 Vehicle Condition/Warranty Exclusion.</strong> Guest agrees Guest
                received the Vehicle and any Extras in good physical and mechanical condition
                (unless otherwise noted in a pre-trip inspection report). Guest is taking possession
                of the Vehicle and any Extras "as-is" after having an adequate opportunity to
                inspect them. The Company explicitly excludes all express and implied warranties,
                including merchantability or fitness for a particular purpose. Guest agrees not to
                alter or tamper with the vehicle or any Extras. If Guest or Authorized Driver(s)
                determines the Vehicle or any Extra is unsafe, Guest or Authorized Driver(s) shall
                stop operating the Vehicle and any Extra and notify the Company immediately.
              </Text>
              <Text>
                <strong>2.3 Personal property, including personal information and data.</strong> The
                Company is not responsible for damage, loss, or theft of personal property or data,
                whether during or after the trip, regardless of fault. No bailment is created and
                that guests acknowledge personal data downloaded to the vehicle may not be secure or
                accessible after the reservation period. Guests release the Company from liability
                related to such data.
              </Text>
              <Text>
                <strong>2.4 Vehicle Return; Late Returns.</strong> Guest agrees to return the
                Vehicle and any Extras to the Company by the return date or on demand, in the same
                condition as received (ordinary wear and tear excepted). Extensions are at the
                Company's option. If the Vehicle is operated after termination, the Company can
                notify police of embezzlement/theft. Guests release and indemnify the Company from
                liability arising from such notice. In the state of Arizona, in accordance with
                A.R.S. 13-1806, if a Guest fails to return a Vehicle within 72 hours after the
                scheduled end of Reservation Period, {company} and/or Host will report it stolen.
                Failure to return a Vehicle in the state of Arizona within this time period is a
                class 5 felony.
              </Text>
              <Text>
                <strong>2.5 Guest Financial Responsibility.</strong> The Guest is financially
                responsible for physical damage or theft of the Vehicle during the Reservation
                Period, regardless of fault, and this applies whether the Guest has their own auto
                policy or not. Guests must work with the Company to make a claim under any
                applicable auto policy. Any protection plan selected by the Guest will not be
                available until all personal coverage options have been exhausted.
              </Text>
              <Text>
                <strong>
                  2.7 Guests Resident in the State of Florida; Guests With a Booked Trip in the
                  State of Florida; Guests Insured in the State of Florida.
                </strong>{' '}
                The valid and collectible insurance and personal injury protection insurance of any
                authorized rental or leasing driver is primary for the limits of the liability and
                personal injury protection coverage required by ss.324.012 (7) and (627.736),
                Florida Statutes.
              </Text>
            </Section>

            {/* 6. GENERAL */}
            <Section id="general">
              <SectionTitle>3. GENERAL</SectionTitle>
              <Text>
                If any provision of the Agreement is held invalid, illegal, or unenforceable, the
                validity, legality, and enforceability of the remaining provisions will not be
                affected. A waiver by either party of any right or a breach does not constitute a
                waiver of any other right or breach. This Agreement cannot be altered by another
                document or oral agreement unless agreed to in writing or through the Company's
                services.
              </Text>
            </Section>

            {/* 7. SIGNATURE SECTION */}
            <Section id="signature">
              <SignatureSection>
                <SignatureContainer>
                  <Text>
                    <strong>Renter Signature</strong>
                  </Text>
                  <MarginTop10>
                    <SignatureLine />
                  </MarginTop10>
                </SignatureContainer>
                <DateContainer>
                  <Text>
                    <strong>Date:</strong>
                  </Text>
                  <MarginTop10>
                    <DateLine />
                  </MarginTop10>
                </DateContainer>
              </SignatureSection>
            </Section>
          </AgreementContainer>
        </Container>
        <PrintButton onClick={handlePrint}>Print Agreement</PrintButton>
      </PageContainer>
    </>
  );
};
